import React from 'react';
import Icon from '../../../components/AppIcon';

const ReviewProgressIndicator = ({
  currentIndex = 0,
  totalMatches = 0,
  completedReviews = 0,
  onNavigate = () => {},
  className = ''
}) => {
  const progressPercentage = totalMatches > 0 ? (completedReviews / totalMatches) * 100 : 0;
  const remainingMatches = totalMatches - completedReviews;

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < totalMatches - 1;

  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${className}`}>
      <div className="flex flex-col space-y-4">
        {/* Progress Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-foreground">
            Review Progress
          </h3>
          <span className="text-xs text-muted-foreground">
            {completedReviews} of {totalMatches} reviewed
          </span>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300 ease-smooth"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{Math.round(progressPercentage)}% Complete</span>
            <span>{remainingMatches} remaining</span>
          </div>
        </div>

        {/* Current Position */}
        <div className="flex items-center justify-between py-2 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Eye" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">
              Match {currentIndex + 1} of {totalMatches}
            </span>
          </div>
          
          {/* Navigation Controls */}
          <div className="flex items-center space-x-1">
            <button
              onClick={() => onNavigate(currentIndex - 1)}
              disabled={!canGoPrevious}
              className={`
                p-1.5 rounded transition-colors duration-150
                ${canGoPrevious 
                  ? 'text-foreground hover:bg-muted' 
                  : 'text-muted-foreground cursor-not-allowed'
                }
              `}
              title="Previous match"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            
            <button
              onClick={() => onNavigate(currentIndex + 1)}
              disabled={!canGoNext}
              className={`
                p-1.5 rounded transition-colors duration-150
                ${canGoNext 
                  ? 'text-foreground hover:bg-muted' 
                  : 'text-muted-foreground cursor-not-allowed'
                }
              `}
              title="Next match"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border">
          <div className="text-center">
            <div className="text-lg font-semibold text-success">
              {completedReviews}
            </div>
            <div className="text-xs text-muted-foreground">
              Reviewed
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-semibold text-warning">
              {remainingMatches}
            </div>
            <div className="text-xs text-muted-foreground">
              Remaining
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-semibold text-primary">
              {totalMatches}
            </div>
            <div className="text-xs text-muted-foreground">
              Total
            </div>
          </div>
        </div>

        {/* Time Estimate */}
        {remainingMatches > 0 && (
          <div className="pt-2 border-t border-border">
            <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
              <Icon name="Clock" size={12} />
              <span>
                Est. {Math.ceil(remainingMatches * 1.5)} min remaining
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewProgressIndicator;